import React from 'react';
import { PageBackLink, type PageBackLinkProps } from './PageBackLink';

interface PageHeaderProps {
  title: string;
  description?: React.ReactNode;
  /** Optional lead-in above the title (uses kicker styling) */
  eyebrow?: string;
  /** Destination-aware return link above the title block */
  backLink?: PageBackLinkProps;
  /** Right-side actions (Create, Filters, etc.) */
  actions?: React.ReactNode;
  className?: string;
  /** Use denser spacing under sticky toolbars */
  compact?: boolean;
}

/**
 * Shared page title block for Discover / Events / Clubs and similar list pages.
 * Keeps hierarchy consistent with the Bilt:Spec design system (Outfit display + muted support).
 */
export const PageHeader: React.FC<PageHeaderProps> = ({
  title,
  description,
  eyebrow,
  backLink,
  actions,
  className = '',
  compact = false,
}) => {
  return (
    <header
      className={`flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 ${
        compact ? 'mb-5' : 'mb-7 sm:mb-10'
      } ${className}`}
    >
      <div className="min-w-0">
        {backLink ? (
          <div className="mb-3">
            <PageBackLink {...backLink} />
          </div>
        ) : null}
        {eyebrow ? <p className="kicker mb-2">{eyebrow}</p> : null}
        <h1 className="heading-display tracking-tight text-balance">{title}</h1>
        {description ? (
          <p className="text-muted text-sm sm:text-base mt-2.5 max-w-2xl leading-relaxed">
            {description}
          </p>
        ) : null}
      </div>
      {actions ? (
        <div className="flex w-full min-w-0 flex-wrap items-center gap-2 sm:w-auto sm:shrink-0">
          {actions}
        </div>
      ) : null}
    </header>
  );
};

export default PageHeader;
